import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { createBlogPost, updateBlogPost, getBlogPosts, getMachines } from '../services/apiService';
import ImageUploader from '../components/ImageUploader';
import GalleryUploader from '../components/GalleryUploader';

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featuredImage: '',
    images: [],
    tags: [],
    youtubeUrl: '',
    relatedMachines: [],
    author: 'AKM Laser',
    published: true,
    metaDescription: ''
  });

  const [newTag, setNewTag] = useState('');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMachines();
    if (isEdit && id) {
      fetchBlogPost();
    }
  }, [isEdit, id]);

  const fetchMachines = async () => {
    try {
      const data = await getMachines();
      setMachines(data);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const fetchBlogPost = async () => {
    try {
      const posts = await getBlogPosts();
      const post = posts.find(p => p.id === id);
      if (post) {
        setFormData(post);
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Erreur lors du chargement');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Auto-generate slug from title
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: isEdit ? prev.slug : title
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleMachineToggle = (machineId) => {
    setFormData(prev => ({
      ...prev,
      relatedMachines: prev.relatedMachines.includes(machineId)
        ? prev.relatedMachines.filter(id => id !== machineId)
        : [...prev.relatedMachines, machineId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        await updateBlogPost(id, formData);
      } else {
        await createBlogPost(formData);
      }

      navigate('/admin/dashboard?tab=blog');
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/dashboard?tab=blog')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au dashboard
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
            {isEdit ? 'Modifier l\'article' : 'Nouvel article de blog'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title & Slug */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Titre *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                  placeholder="Guide complet du laser CO2"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Slug (URL) *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                  placeholder="guide-complet-laser-co2"
                />
                <p className="text-xs text-gray-500 mt-1">URL: /blog/{formData.slug || '...'}</p>
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-semibold mb-2">Résumé (pour la liste) *</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                required
                rows="2"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                placeholder="Courte description affichée dans la liste des articles..."
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-semibold mb-2">Contenu complet *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows="12"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none font-mono text-sm"
                placeholder="Contenu détaillé de l'article..."
              />
            </div>

            {/* Featured Image */}
            <div>
              <ImageUploader
                label="Image principale"
                required
                value={formData.featuredImage}
                onChange={(v) => setFormData((prev) => ({ ...prev, featuredImage: v }))}
                hint="Image qui apparaîtra en couverture de l'article"
              />
            </div>

            {/* Additional Images */}
            <div>
              <GalleryUploader
                label="Images supplémentaires"
                value={formData.images}
                onChange={(arr) => setFormData((prev) => ({ ...prev, images: arr }))}
                maxImages={10}
              />


            {/* Lien YouTube */}
            <div>
              <label className="block text-sm font-semibold mb-2">
                Lien YouTube (optionnel)
              </label>
              <input
                type="url"
                name="youtubeUrl"
                value={formData.youtubeUrl || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-gray-500 mt-1">
                📹 La vidéo YouTube s'affichera dans l'article de blog
              </p>
            </div>

            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-semibold mb-2">Tags SEO</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                  placeholder="Laser CO2"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 rounded-lg text-white"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full flex items-center space-x-2"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-amber-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Related Machines */}
            <div>
              <label className="block text-sm font-semibold mb-2">Machines associées (optionnel)</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                {machines.map(machine => (
                  <label key={machine.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.relatedMachines.includes(machine.id)}
                      onChange={() => handleMachineToggle(machine.id)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{machine.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Meta Description */}
            <div>
              <label className="block text-sm font-semibold mb-2">Meta Description SEO</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                placeholder="Description pour les moteurs de recherche (150-160 caractères)"
                maxLength="160"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.metaDescription?.length || 0}/160 caractères</p>
            </div>

            {/* Author & Published */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Auteur</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="published"
                    checked={formData.published}
                    onChange={handleChange}
                    className="w-5 h-5 mr-2"
                  />
                  <span className="font-semibold">Article publié</span>
                </label>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard?tab=blog')}
                className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#5C3A21' }}
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogForm;
